// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the "From Konvo" list (docs/konvo-assistant/ready-list.md, mockup b-feed-r3): everything
// Konvo has ready today (corner pop-ups and the engine's briefs and nudges, one shape from GET
// /konvo/corner/list) plus Done today. Loads once the app has settled, a second after a
// KONVO_CORNER_UPDATE (Corner.applyUpdate), on focus (not within 30 s of the last load), 1.5 s
// after the assistant posts in its DM and every 5 minutes while visible. A pop-up that closes by
// itself folds in here: the icon shows the count and a one-line peek for a minute. What is acted
// on here leaves at once (or moves to Done today) while the server catches up.

import MobileRailPanelState from '@app/features/app/components/layout/mobile_rail/MobileRailPanelState';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import Channels from '@app/features/channel/state/Channels';
import {assistantChannelIdNow} from '@app/features/konvo_assistant/state/AssistantUnread';
import AskChangeSignal from '@app/features/konvo_corner/state/AskChangeSignal';
import {
	applyLocalChanges,
	itemForSuggestion,
	type LocalChange,
	parseReadyList,
	peekHeadline,
	withFolded,
} from '@app/features/konvo_corner/state/ReadyListModel';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {CoalescedLoad} from '@app/features/platform/transport/CoalescedLoad';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {afterStartup} from '@app/features/platform/utils/StartupScheduler';
import {VisiblePoll} from '@app/features/ui/utils/VisiblePoll';
import Users from '@app/features/user/state/Users';
import Window from '@app/features/window/state/Window';
import type {CornerSuggestion, ReadyItem} from '@konvo/schema/src/domains/konvo/AssistantEngagement';
import {makeAutoObservable, observable, reaction, runInAction} from 'mobx';

const logger = new Logger('ReadyList');

/** Focus within this long of the last load reuses it. */
const FOCUS_GAP_MS = 30_000;
const POLL_MS = 5 * 60_000;
/** Corner pushes come in bursts (planned, shown, outcome echoes): one load a second later. */
const UPDATE_DEBOUNCE_MS = 1_000;
/** Give the engine a moment to record the item it just posted in the Assistant DM (as Home). */
const AFTER_MESSAGE_MS = 1_500;
/** The one-line peek beside the icon after a pop-up folded. */
export const PEEK_MS = 60_000;

export class ReadyListStore {
	/** The server's Ready for you, newest first. */
	serverItems: Array<ReadyItem> = [];
	serverDone: Array<ReadyItem> = [];
	loaded = false;
	/** Desktop: the list above the icon is open (on a phone the rail's 'ready' panel is the list). */
	listOpen = false;
	/** The peek beside the icon after a pop-up folded (until `until`). */
	peek: {itemId: string; until: number} | null = null;
	/** Acted on here: hidden from Ready (or shown under Done) while the server catches up. */
	changes = observable.map<string, LocalChange>();
	/** Ticks when a local change runs out, so the getters look again. */
	now = Date.now();

	private started = false;
	private generation = 0;
	private loads = new CoalescedLoad(FOCUS_GAP_MS);
	private stops: Array<() => void> = [];
	private soon: ReturnType<typeof setTimeout> | null = null;
	private peekTimer: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		makeAutoObservable<this, 'started' | 'generation' | 'loads' | 'stops' | 'soon' | 'peekTimer'>(
			this,
			{
				peek: observable.ref,
				serverItems: observable.ref,
				serverDone: observable.ref,
				started: false,
				generation: false,
				loads: false,
				stops: false,
				soon: false,
				peekTimer: false,
			},
			{autoBind: true},
		);
	}

	private get view() {
		return applyLocalChanges(this.serverItems, this.serverDone, this.changes, this.now);
	}

	/** The list shows: above the corner icon, or grown out of the phone rail. */
	get isOpen(): boolean {
		return this.listOpen || MobileRailPanelState.open === 'ready';
	}

	/** Ready for you, newest first, less what was acted on here. */
	get items(): Array<ReadyItem> {
		return this.view.items;
	}

	/** Done today, newest first. */
	get done(): Array<ReadyItem> {
		return this.view.done;
	}

	/** The number on the icon (and on the phone's Ask button). */
	get count(): number {
		return this.items.length;
	}

	/** The peek's line, while it shows: the headline of the one that folded (or the newest). */
	get peekLine(): {count: number; headline: string} | null {
		const peek = this.peek;
		if (!peek || this.isOpen || this.count === 0) return null;
		const headline = peekHeadline(this.items, peek.itemId);
		return headline ? {count: this.count, headline} : null;
	}

	/** Start with the corner (Corner.start); returns the stop. */
	start(): () => void {
		if (this.started) return () => {};
		this.started = true;
		const poll = new VisiblePoll(POLL_MS, () => this.load(true), false, POLL_MS);
		poll.start();
		const stopFocus = reaction(
			() => Window.focused && Window.visible,
			(active) => {
				if (active) void this.load();
			},
		);
		let messageTimer: ReturnType<typeof setTimeout> | null = null;
		// The assistant posting in its DM (a brief, a nudge): it may belong in the list.
		const stopDm = reaction(
			() => {
				const channelId = assistantChannelIdNow();
				return channelId ? `${channelId}:${Channels.getChannel(channelId)?.lastMessageId ?? ''}` : null;
			},
			(key) => {
				if (!key) return;
				const [channelId, messageId] = key.split(':');
				if (!channelId || !messageId) return;
				const authorId = Messages.getMessage(channelId, messageId)?.author.id;
				if (authorId && authorId === Users.currentUserId) return;
				if (messageTimer) clearTimeout(messageTimer);
				messageTimer = setTimeout(() => void this.load(true), AFTER_MESSAGE_MS);
			},
		);
		this.stops = [
			() => poll.stop(),
			stopFocus,
			stopDm,
			() => {
				if (messageTimer) clearTimeout(messageTimer);
			},
			afterStartup(
				() => {
					if (Window.visible) void this.load(true);
				},
				{delayMs: 2000, jitterMs: 3000},
			),
		];
		return () => this.stop();
	}

	stop(): void {
		for (const stop of this.stops) stop();
		this.stops = [];
		this.started = false;
		this.reset();
	}

	/** Sign-out or account switch: forget the list. */
	reset(): void {
		this.generation += 1;
		if (this.soon) clearTimeout(this.soon);
		this.soon = null;
		if (this.peekTimer) clearTimeout(this.peekTimer);
		this.peekTimer = null;
		this.serverItems = [];
		this.serverDone = [];
		this.loaded = false;
		this.listOpen = false;
		this.peek = null;
		this.changes.clear();
		this.loads.reset();
	}

	load(force = false): Promise<void> {
		if (!this.started) return Promise.resolve();
		return this.loads.run(() => this.fetch(), {force});
	}

	/** Load again shortly (a burst of corner pushes, an action just taken). */
	refreshSoon(delayMs = UPDATE_DEBOUNCE_MS): void {
		if (!this.started) return;
		if (this.soon) clearTimeout(this.soon);
		this.soon = setTimeout(() => {
			this.soon = null;
			void this.load(true);
		}, delayMs);
	}

	private async fetch(): Promise<void> {
		const generation = this.generation;
		const requestedAt = Date.now();
		try {
			const response = await http.get<unknown>(Endpoints.KONVO_CORNER_LIST, {mode: 'silent'});
			if (generation !== this.generation) return;
			if (response.ok !== false) AskChangeSignal.report(response.body, requestedAt);
			const parsed = response.ok === false ? null : parseReadyList(response.body);
			if (!parsed) return;
			runInAction(() => {
				this.serverItems = parsed.items;
				this.serverDone = parsed.done;
				this.loaded = true;
				this.now = Date.now();
			});
		} catch (error) {
			logger.warn('Could not load the From Konvo list', error);
		}
	}

	/** Desktop: open the list above the corner icon. */
	open(): void {
		this.listOpen = true;
		this.opened();
	}

	/** Phone: the rail grows into the list, as it does for Ask. */
	openInRail(): void {
		MobileRailPanelState.openReady();
		this.opened();
	}

	private opened(): void {
		this.peek = null;
		this.now = Date.now();
		void this.load();
	}

	close(): void {
		this.listOpen = false;
		if (MobileRailPanelState.open === 'ready') MobileRailPanelState.close();
	}

	toggle(): void {
		if (this.isOpen) this.close();
		else this.open();
	}

	/** A pop-up closed by itself: it is in the list now (at once if it carries a ready card). */
	fold(suggestion: CornerSuggestion): void {
		const item = itemForSuggestion(suggestion, new Date().toISOString());
		if (item && !this.serverItems.some((entry) => entry.id === item.id)) {
			this.serverItems = withFolded(this.serverItems, item);
		}
		this.changes.delete(`c:${suggestion.id}`);
		this.peek = {itemId: `c:${suggestion.id}`, until: Date.now() + PEEK_MS};
		if (this.peekTimer) clearTimeout(this.peekTimer);
		this.peekTimer = setTimeout(() => this.clearPeek(), PEEK_MS);
		this.refreshSoon(300);
	}

	clearPeek(): void {
		if (this.peekTimer) clearTimeout(this.peekTimer);
		this.peekTimer = null;
		this.peek = null;
	}

	/** Acted on or dismissed here: out of Ready for you at once. */
	remove(item: ReadyItem): void {
		this.mark(item, null);
	}

	/** Done here ("Sent", "Done"): moves to Done today at once. */
	complete(item: ReadyItem, note: string): void {
		this.mark(item, note);
	}

	/** Undo, or the action failed: back in Ready for you. */
	restore(item: ReadyItem): void {
		this.changes.delete(item.id);
		if (!this.serverItems.some((entry) => entry.id === item.id)) {
			this.serverItems = withFolded(this.serverItems, {...item, state: 'ready'});
		}
		this.now = Date.now();
	}

	private mark(item: ReadyItem, doneNote: string | null): void {
		const at = Date.now();
		this.now = at;
		this.changes.set(item.id, {at, doneNote, item});
		if (this.peek?.itemId === item.id && this.count === 0) this.clearPeek();
		// Look again once the hold runs out, so a server that still lists it shows it again.
		setTimeout(() => runInAction(() => (this.now = Date.now())), 61_000);
	}
}

export default new ReadyListStore();
